import { findDedupMatches, type DedupQuery, type MatchCandidate } from '@/modules/lab/catalog/catalogDedup';
import type { CatalogInvestigationRow } from '@/modules/lab/catalog/types';
import { useEffect, useMemo, useRef, useState } from 'react';

const DEBOUNCE_MS = 200;

export type DedupCheckState = {
  matches: MatchCandidate[];
  checking: boolean;
  hasHighConfidenceMatch: boolean;
  topMatch: MatchCandidate | null;
};

export function useDedupCheck(
query: DedupQuery,
catalog: CatalogInvestigationRow[],
enabled = true)
: DedupCheckState {
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [checking, setChecking] = useState(false);
  const generation = useRef(0);

  const queryKey = useMemo(
    () =>
    JSON.stringify({
      name: query.name?.trim() ?? '',
      shortCode: query.shortCode?.trim() ?? '',
      aliases: (query.aliases ?? []).map((a) => a.trim()).filter(Boolean).sort()
    }),
    [query.name, query.shortCode, query.aliases]
  );

  useEffect(() => {
    if (!enabled) {
      setDebouncedQuery({ name: '', aliases: [], shortCode: '' });
      setChecking(false);
      return;
    }

    setChecking(true);
    const gen = ++generation.current;
    const timer = window.setTimeout(() => {
      if (gen !== generation.current) return;
      setDebouncedQuery(query);
      setChecking(false);
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- compare generation at cleanup time
      if (gen === generation.current) setChecking(false);
    };
  }, [queryKey, enabled, query]);

  const matches = useMemo(() => {
    if (!enabled) return [];
    const hasInput =
    (debouncedQuery.name?.trim() ?? '').length > 0 ||
    (debouncedQuery.shortCode?.trim() ?? '').length > 0 || (
    debouncedQuery.aliases?.some((a) => a.trim()) ?? false);
    if (!hasInput) return [];
    return findDedupMatches(debouncedQuery, catalog, 5);
  }, [debouncedQuery, catalog, enabled]);

  const topMatch = matches[0] ?? null;
  const hasHighConfidenceMatch = matches.some((m) => m.score >= 0.95);

  return { matches, checking, hasHighConfidenceMatch, topMatch };
}